import React, { useEffect, useState } from 'react'
import { $MAX_WIDTH } from '../Consts'
import { getInitialLang } from '../Common'

type Lang = 'en' | 'zh'

const demoEndpoint = 'https://book-demo.serverless.businessa.com'

const EN = {
  eyebrow: 'Book a Demo',
  h1: 'Find out how BusinessA can help you',
  p: 'Tell us what you want to build. We will show you how BusinessA can build it with maximal control and pixel-perfect accuracy.',
  submit: 'Request Demo',
  sending: 'Submitting...',
  error: 'Oops – something went wrong. Please try again.',
  successTitle: 'Thanks, your demo request has been received',
  successText:
    'We will review your app idea and follow up with a tailored walkthrough of how BusinessA can help you build it.',
  labels: {
    name: 'Name',
    email: 'Work email',
    message: 'What do you want to build?',
  },
  placeholders: {
    name: 'Your name',
    email: 'you@company.com',
    message:
      'Describe the app, workflow, portal, dashboard or internal tool you want to build.',
  },
  successEyebrow: 'Request received',
}

const ZH = {
  eyebrow: '预约演示',
  h1: '看看 BusinessA 精确构建你的应用',
  p: '告诉我们你想构建什么。我们会展示 BusinessA 如何提供页面级 AI 控制、插件支持和可靠的业务应用结构。',
  submit: '提交演示请求',
  sending: '提交中...',
  error: '抱歉，发生错误，请重试。',
  successTitle: '谢谢，你的演示请求已收到',
  successText:
    '我们会查看你的应用想法，并为你准备一场有针对性的演示，展示 BusinessA 如何帮助你构建它。',
  labels: {
    name: '姓名',
    email: '工作邮箱',
    message: '你想构建什么？',
  },
  placeholders: {
    name: '你的姓名',
    email: 'you@company.com',
    message: '描述你想构建的应用、工作流、门户、仪表盘或内部工具。',
  },
  successEyebrow: '请求已收到',
}

const LANGS = { en: EN, zh: ZH }

const createInitialForm = () => ({
  name: '',
  email: '',
  message: '',
})

const useDesktop = () => {
  const [desktop, setDesktop] = useState(
    () =>
      typeof window === 'undefined' ||
      window.matchMedia('(min-width:1024px)').matches
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    const media = window.matchMedia('(min-width:1024px)')
    const update = () => setDesktop(media.matches)

    update()

    if (media.addEventListener) {
      media.addEventListener('change', update)
      return () => media.removeEventListener('change', update)
    }

    media.addListener(update)
    return () => media.removeListener(update)
  }, [])

  return desktop
}

const BookDemoPage: React.FC = () => {
  const desktop = useDesktop()
  const [lang] = useState<Lang>(getInitialLang)
  const [submitted, setSubmitted] = useState(false)
  const [status, setStatus] = useState<
    'idle' | 'sending' | 'success' | 'error'
  >('idle')
  const [buttonHover, setButtonHover] = useState(false)
  const [form, setForm] = useState(createInitialForm)

  const t = LANGS[lang]

  const grid =
    'repeating-linear-gradient(to right,rgba(255,255,255,.065) 0 1px,transparent 1px 80px),repeating-linear-gradient(to bottom,rgba(255,255,255,.065) 0 1px,transparent 1px 80px)'

  const inputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    padding: desktop ? '1.2rem 1.25rem' : '1rem 1rem',
    borderRadius: 18,
    border: '1px solid rgba(255,255,255,.28)',
    background: 'rgba(255,255,255,.14)',
    color: '#fff',
    outline: 'none',
    fontSize: desktop ? '1.05rem' : '1rem',
    fontFamily: 'inherit',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,.1)',
  }

  const labelStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '.7rem',
    fontSize: '1rem',
    fontWeight: 800,
    color: 'rgba(255,255,255,.94)',
  }

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target
    setForm((current) => ({ ...current, [name]: value }))
    if (status === 'error') {
      setStatus('idle')
    }
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (status === 'sending') return

    setStatus('sending')

    try {
      const res = await fetch(demoEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })

      if (!res.ok) throw new Error('Network error')

      setStatus('success')
      setSubmitted(true)
      setForm(createInitialForm())

      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    } catch {
      setStatus('error')
    }
  }

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100vh',
        boxSizing: 'border-box',
        background:
          'linear-gradient(180deg,#4fa6e5 0%,#3289dc 34%,#266bc8 68%,#102b70 100%)',
        color: '#fff',
        fontFamily:
          'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <style>
        {`
          .book-demo-control::placeholder {
            color: rgba(255,255,255,.92);
            opacity: 1;
          }

          .book-demo-control::-ms-input-placeholder {
            color: rgba(255,255,255,.92);
          }

          .book-demo-control:focus {
            border-color: rgba(255,255,255,.62);
            box-shadow: inset 0 1px 0 rgba(255,255,255,.12), 0 0 0 4px rgba(255,255,255,.1);
          }

          .book-demo-control option {
            color: #123578;
            background: #fff;
          }

          .book-demo-control option:disabled {
            color: #6f7fa2;
          }
        `}
      </style>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: grid,
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          width: 620,
          height: 620,
          borderRadius: '50%',
          background:
            'radial-gradient(circle,rgba(118,206,255,.42),rgba(118,206,255,0) 68%)',
          top: -220,
          right: -190,
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          width: 640,
          height: 640,
          borderRadius: '50%',
          background:
            'radial-gradient(circle,rgba(74,112,255,.32),rgba(74,112,255,0) 70%)',
          bottom: -260,
          left: -230,
          pointerEvents: 'none',
        }}
      />

      <section
        style={{
          maxWidth: $MAX_WIDTH,
          width: '100%',
          margin: '0 auto',
          padding: desktop ? '7rem 2rem 6rem' : '5rem 1.25rem 4rem',
          boxSizing: 'border-box',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <header
          style={{
            maxWidth: 980,
            margin: '0 auto',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              fontSize: desktop ? '1rem' : '.92rem',
              fontWeight: 850,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,.82)',
              marginBottom: '1rem',
            }}
          >
            {t.eyebrow}
          </div>

          <h1
            style={{
              fontSize: desktop ? '4.75rem' : '2.65rem',
              fontWeight: 900,
              lineHeight: 1.02,
              letterSpacing: '-.06em',
              margin: 0,
            }}
          >
            {t.h1}
          </h1>

          <p
            style={{
              fontSize: desktop ? '1.22rem' : '1.04rem',
              opacity: 0.92,
              maxWidth: 790,
              margin: '1.45rem auto 0',
              lineHeight: 1.75,
            }}
          >
            {t.p}
          </p>
        </header>

        <div
          style={{
            width: '100%',
            maxWidth: 1120,
            margin: desktop ? '4rem auto 0' : '2.75rem auto 0',
            borderRadius: desktop ? 38 : 28,
            padding: desktop ? '1.55rem' : '.9rem',
            background:
              'linear-gradient(180deg,rgba(255,255,255,.2),rgba(255,255,255,.08))',
            border: '1px solid rgba(255,255,255,.28)',
            boxShadow: '0 34px 90px rgba(0,0,0,.24)',
            backdropFilter: 'blur(22px)',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              borderRadius: desktop ? 30 : 22,
              padding: desktop ? '3rem' : '1.35rem',
              background: 'rgba(7,24,68,.42)',
              border: '1px solid rgba(255,255,255,.16)',
              boxSizing: 'border-box',
            }}
          >
            {submitted ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: desktop ? '3.85rem 1rem' : '2.5rem .5rem',
                  maxWidth: 720,
                  margin: '0 auto',
                }}
              >
                <div
                  style={{
                    width: desktop ? 96 : 84,
                    height: desktop ? 96 : 84,
                    borderRadius: desktop ? 30 : 26,
                    background:
                      'linear-gradient(135deg,rgba(255,255,255,.24),rgba(255,255,255,.08))',
                    border: '1px solid rgba(255,255,255,.3)',
                    boxShadow:
                      '0 24px 60px rgba(0,0,0,.24), inset 0 1px 0 rgba(255,255,255,.22)',
                    margin: '0 auto 1.7rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg
                    width={desktop ? 54 : 48}
                    height={desktop ? 54 : 48}
                    viewBox="0 0 56 56"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M15 29.5L24.2 38.5L42 18.5"
                      stroke="url(#bookDemoSuccessCheck)"
                      strokeWidth="5.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <defs>
                      <linearGradient
                        id="bookDemoSuccessCheck"
                        x1="15"
                        y1="18.5"
                        x2="42"
                        y2="38.5"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#8fffea" />
                        <stop offset="1" stopColor="#ffffff" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>

                <h2
                  style={{
                    margin: 0,
                    fontSize: desktop ? '2.55rem' : '1.78rem',
                    letterSpacing: '-.045em',
                    lineHeight: 1.1,
                  }}
                >
                  {t.successTitle}
                </h2>
                <p
                  style={{
                    margin: '1.15rem auto 0',
                    maxWidth: 620,
                    fontSize: desktop ? '1.08rem' : '1rem',
                    lineHeight: 1.75,
                    opacity: 0.88,
                  }}
                >
                  {t.successText}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: desktop ? '1fr 1fr' : '1fr',
                    gap: desktop ? '1.55rem 1.75rem' : '1.1rem',
                  }}
                >
                  <label style={labelStyle}>
                    {t.labels.name}
                    <input
                      className="book-demo-control"
                      name="name"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder={t.placeholders.name}
                      style={inputStyle}
                    />
                  </label>

                  <label style={labelStyle}>
                    {t.labels.email}
                    <input
                      className="book-demo-control"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder={t.placeholders.email}
                      style={inputStyle}
                    />
                  </label>
                </div>

                <label
                  style={{
                    ...labelStyle,
                    marginTop: desktop ? '1.75rem' : '1.15rem',
                  }}
                >
                  {t.labels.message}
                  <textarea
                    className="book-demo-control"
                    name="message"
                    required
                    value={form.message}
                    onChange={handleChange}
                    placeholder={t.placeholders.message}
                    style={{
                      ...inputStyle,
                      minHeight: desktop ? 190 : 160,
                      resize: 'vertical',
                      lineHeight: 1.65,
                    }}
                  />
                </label>

                <button
                  type="submit"
                  style={{
                    width: '100%',
                    marginTop: desktop ? '1.9rem' : '1.35rem',
                    background: buttonHover
                      ? 'linear-gradient(90deg,#ffffff 0%,#dcecff 100%)'
                      : 'linear-gradient(90deg,#ffffff 0%,#eef6ff 100%)',
                    color: '#123578',
                    padding: desktop ? '1.25rem 2rem' : '1.08rem 2rem',
                    fontSize: desktop ? '1.1rem' : '1.04rem',
                    fontWeight: 900,
                    border: 'none',
                    borderRadius: 18,
                    cursor: status === 'sending' ? 'not-allowed' : 'pointer',
                    opacity: status === 'sending' ? 0.68 : 1,
                    boxShadow: buttonHover
                      ? '0 20px 42px rgba(0,0,0,.23)'
                      : '0 14px 32px rgba(0,0,0,.17)',
                    transition: 'box-shadow .25s, transform .25s, background .25s',
                    transform: buttonHover ? 'translateY(-2px)' : 'translateY(0)',
                  }}
                  onMouseEnter={() => setButtonHover(true)}
                  onMouseLeave={() => setButtonHover(false)}
                  disabled={status === 'sending'}
                >
                  {status === 'sending' ? t.sending : t.submit}
                </button>

                {status === 'error' && (
                  <p
                    style={{
                      margin: '1rem 0 0',
                      color: '#ffd8d8',
                      fontSize: desktop ? '1rem' : '.95rem',
                      fontWeight: 800,
                      textAlign: 'center',
                    }}
                  >
                    {t.error}
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}

export default BookDemoPage
